import dayjs from "dayjs";
import { LocationTypes } from "@/utils/constants/LocationsConstants";
import {
  Devices,
  SegmentTypeDevices,
  customSegmentTypeDevice,
} from "@/utils/constants/DevicesConstants";
import {
  InteractionTypeItems,
  MatchTypeItems,
  MatchTypeItemsAlter,
  RangeTypes,
  RangeTypesBase,
  RawUsageTimePeriods,
  TimeDuration,
  TrendDirection,
  UserBucketItems,
  UserFilterItems,
} from "@/utils/constants/SegmentsConstants";
import { dateDisplay } from "@/utils/DateDisplayUtils";

const customValueDisplay = (value, dataType) => {
  if (!value) return "";
  if (dataType === "date") return dayjs(value).isValid() ? dateDisplay(value) : value;
  if (dataType === "boolean") return value === "1" ? "True" : value === "0" ? "False" : "Null";
  return value;
};

const customValueArrayOrStringDisplay = (values, dataType) => {
  if (Array.isArray(values)) {
    const localValues = values.map(val => customValueDisplay(val, dataType));
    return `${localValues.slice(0, 4).join(", ")}${localValues.length > 4 ? "..." : ""}`;
  }
  return customValueDisplay(values, dataType);
};

export const getCustomFilter = (custom, customFilters) =>
  Object.entries(custom)
    .filter(([_, v]) =>
      Array.isArray(v) ? v?.filter(vv => vv?.values?.length).length : v?.values?.length,
    )
    .flatMap(([k, v]) => {
      const filter = customFilters.find(p => p.label === k);
      if (Array.isArray(v)) {
        return [
          ["Custom", filter.name],
          ...v.map(vv => {
            const value = customValueArrayOrStringDisplay(vv.values, filter.data_type);
            return [MatchTypeItems[vv.options?.negative_match]?.text, value];
          }),
        ];
      }
      const value = customValueArrayOrStringDisplay(v.values, filter.data_type);
      return [
        ["Custom", filter.name],
        [MatchTypeItems[v.options?.negative_match]?.text, value],
      ];
    });

export const getUserFilter = user =>
  Object.values(UserFilterItems)
    .filter(el => user?.[el.value]?.length)
    .map(el => [
      `Users ${el.text.toLowerCase()}`,
      `From ${user[el.value][0]} to ${user[el.value][1]}`,
    ]);

export const getUserBucketFilter = userBucket => {
  if (!userBucket || !userBucket.range_type || !userBucket.range.length) return [];

  return [
    ["User", UserBucketItems.range.text],
    [
      RangeTypesBase[userBucket.range_type]?.text,
      userBucket.range.filter(el => !["INF", "0"].includes(`${el}`)).join(", "),
    ],
  ];
};

export const getUserIdFilter = userId => {
  if (!userId || !userId.values.length) return [];
  return [
    ["User", UserBucketItems.values.text],
    [
      MatchTypeItems[userId.negative_match]?.text,
      userId.values
        .reduce((acc, el) => {
          if (el) acc.push(`"${el}"`);
          return acc;
        }, [])
        .join(", "),
    ],
  ];
};

export const getLocationFilter = (location, locationConfigs) =>
  Object.values(LocationTypes)
    .filter(el => location?.[el.value]?.length)
    .flatMap(el => [
      ["Location", LocationTypes[el.value].name],
      [
        MatchTypeItems[locationConfigs?.[el.value]?.negative_match]?.text,
        location[el.value].join(", "),
      ],
    ]);

export const getInteractionFilter = promoInteraction => {
  const interactions = {};
  const promoInteractionValues =
    (Array.isArray(promoInteraction) ? promoInteraction[0]?.values : promoInteraction?.values) ||
    [];
  promoInteractionValues.forEach(value => {
    const positiveMatch = MatchTypeItemsAlter[value?.positive_match]?.text;
    const interactionList = value.interaction_types
      .map(el => InteractionTypeItems[el].text)
      .join(", ");
    if (!positiveMatch || !interactionList) return;
    const key = `${positiveMatch} ${interactionList}`;
    interactions[key] = (interactions?.[key] || 0) + 1;
  });

  return Object.entries(interactions).map(([k, v]) => [
    `Interaction, user ${k}`,
    `${v} ${v === 1 ? "prompt" : "prompts"}`,
  ]);
};

export const getDeviceFilter = (device, customDevices) => {
  const res = [];

  [
    SegmentTypeDevices.device_family,
    SegmentTypeDevices.os_family,
    SegmentTypeDevices.browser_family,
  ].forEach(el => {
    if (device?.[el.value]?.length)
      res.push([`Device, ${el.name.toLowerCase()}`, device[el.value].join(", ")]);
  });

  if (device?.[customSegmentTypeDevice.value]?.length) {
    res.push([
      `Device, ${customSegmentTypeDevice.name.toLowerCase()}`,
      device[customSegmentTypeDevice.value]
        .map(el => customDevices.find(cd => cd.label === el)?.name || el)
        .join(", "),
    ]);
  }

  if (device?.[SegmentTypeDevices.device_target.value]?.length)
    res.push([
      `Device, ${SegmentTypeDevices.device_target.name.toLowerCase()}`,
      Devices.filter(el =>
        device.device_target.find(
          e =>
            el.value.device_category === e.device_category &&
            el.value.device_manufacturer === e.device_manufacturer &&
            el.value.device_model === e.device_model &&
            el.value.device_type === e.device_type,
        ),
      )
        .map(e => e.name)
        .join(", "),
    ]);

  return res;
};

const usageTimePeriodString = (range, value) => {
  if (range === RangeTypes.none.value) return "";
  if (range === RangeTypes.trending.value)
    return TimeDuration[value] ? ` ${TimeDuration[value].text || ""}` : "";
  return ` ${RawUsageTimePeriods[value]?.text}`;
};

const usageTimePeriodPrefix = value =>
  RawUsageTimePeriods[value] && value !== RawUsageTimePeriods.null.value ? ", over the last" : "";

const usageRangeString = (range, values, trendDirection) => {
  if ([RangeTypes.none.value, RangeTypes.at_least_once.value].includes(range))
    return RangeTypes[range]?.text.toLowerCase();

  const [min, max] = values;

  if (range === RangeTypes.range.value) return `from ${min} to ${max}`;
  if (range === RangeTypes.less_than_or_equal_to.value)
    return `${RangeTypes[range]?.text.toLowerCase()} ${max}`;
  if (range === RangeTypes.trending.value)
    return `trending from ${min} to ${max} (${TrendDirection[trendDirection].text.toLowerCase()})`;
  return `${RangeTypes[range]?.text.toLowerCase()} ${min}`;
};

export const getUsageFilter = usage =>
  Object.entries(usage).map(([key, filter]) => {
    if (!filter?.length) return [];

    const filterValues =
      (Array.isArray(filter) ? filter.flatMap(el => el.values) : filter?.values) || [];
    if (!filterValues?.length) return [];

    return filter.flatMap(el => [
      `Usage, ${key}, ${usageRangeString(
        el.options.range_type,
        el.values,
        el.options.trend,
      )}${usageTimePeriodPrefix(el.options?.time_period_days)}`,
      usageTimePeriodString(
        el.options.range_type,
        el.options.range_type === "trending" ? el.options.frequency : el.options.time_period_days,
      ),
    ]);
  });

export const getDisplayFilters = (filters, customDevices) =>
  [
    ...getUsageFilter(filters.usage),
    ...getDeviceFilter(filters.device, customDevices),
    ...getLocationFilter(filters.location, filters.locationConfigs),
    ...getUserFilter(filters.user),
    ...getInteractionFilter(filters.promoInteraction),
    ...getCustomFilter(filters.custom, filters.customFilters),
    ...getUserBucketFilter(filters.userBucket),
    ...getUserIdFilter(filters.userId),
  ].filter(a => a.length);

export const getUsageFilterData = segmentCustomFields =>
  !segmentCustomFields?.length
    ? []
    : segmentCustomFields
        .filter(f => f.is_usage)
        .map(f => ({
          ...f,
          name: f.connector_type === "segment" ? `segment.io - ${f.name}` : f.name,
        }));

export const customOrUsageFilters = (data, segmentCustomFilter) =>
  data.reduce((a, { label }) => ({ ...a, [label]: segmentCustomFilter[label] }), {});

export const getCustomFiltersData = segmentCustomFilter =>
  segmentCustomFilter
    .filter(
      field =>
        field.data_type &&
        (field.display_type || field.data_type === "complex_collection") &&
        !field.is_disabled &&
        ["aws_s3", "rsm"].includes(field.connector_type) &&
        field.display_type !== "compound",
    )
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

export const isUserFilterDisabled = (fieldName, segmentSystemFields = []) =>
  (segmentSystemFields.find(({ label }) => label === fieldName) || {})?.is_disabled;

// User Bucket helpers

export const segmentItemFor = (segment, promptOrGuide) =>
  promptOrGuide.segments.find(({ id }) => id === segment.id);

export const bucketRange = segmentItem => segmentItem?.options?.user_bucket?.range;

export function displayBucketRange(segmentItem) {
  const range = bucketRange(segmentItem);
  return range?.length ? range.map(i => Math.ceil(i / 100)) : [0, 100];
}
